<script setup lang="ts">
import { type DecorScreen, nullDecor } from "#ui/decoration/screen";
import KsoWaves from "#ui/templates/kso/ksoWaves/KsoWaves.vue";

export interface Props {
	immersive?: boolean;
	title: string;
	showDiscount?: boolean;
	surcharge?: boolean;
	discountLabel?: string;
	discountValue?: string;
	showDelivery?: boolean;
	deliveryLabel?: string;
	deliveryValue?: string;
	showBonuses?: boolean;
	bonusesLabel?: string;
	bonusesValue?: string;
	totalLabel: string;
	totalValue?: string;
	availableLabel?: string;
	availableValue?: string;
	bonusesOpen?: boolean;
	decor?: DecorScreen;
}

const props = withDefaults(defineProps<Props>(), {
	immersive: true,
	showDiscount: false,
	surcharge: false,
	discountLabel: "",
	discountValue: "",
	showDelivery: false,
	deliveryLabel: "",
	deliveryValue: "",
	showBonuses: false,
	bonusesLabel: "",
	bonusesValue: "",
	totalValue: "",
	availableLabel: "",
	availableValue: "",
	bonusesOpen: false,
	decor: () => nullDecor,
});

const MINUS = "\u2212";
</script>

<template>
	<div class="kso_created tw:flex tw:flex-col tw:w-full" :data-mode="props.immersive ? 'immersive' : 'simple'" v-bind="props.decor.bind('kso_created')">
		<KsoWaves v-if="props.decor.visible('kso_waves', props.immersive)" :decor="props.decor" />
		<div
			class="kso_created_body tw:relative tw:bg-(--accentColor) tw:text-[#fff] tw:flex tw:flex-col tw:gap-16 tw:p-[20px_24px_16px] tw:[[data-mode=simple]>&]:rounded-[var(--kso-panel-radius,24px)_var(--kso-panel-radius,24px)_0_0] tw:[[data-mode=simple]>&]:pt-32 tw:min-[1023px]:gap-22 tw:min-[1023px]:p-[28px_40px_20px]"
			v-bind="props.decor.bind('kso_created_body', { bonuses: props.bonusesOpen })"
		>
			<h2
				class="kso_created_title tw:m-0 tw:text-center tw:[font-family:var(--fontMain)] tw:[font-weight:normal] tw:[font-size:calc(1.25rem*var(--fontFit-x,1))] tw:[line-height:calc(1.3/var(--fontFit-x,1))] tw:min-[1023px]:[font-size:calc(1.9rem*var(--fontFit-x,1))]"
				v-bind="props.decor.bind('kso_title')"
			>
				{{ props.title }}
			</h2>

			<ul class="kso_created_list tw:list-none tw:m-0 tw:p-0 tw:flex tw:flex-col tw:gap-6" v-bind="props.decor.bind('kso_list')">
				<li v-if="props.showDiscount" class="tw:flex tw:items-baseline tw:justify-between tw:gap-12" v-bind="props.decor.bind('kso_row', { discount: true })">
					<p
						class="tw:m-0 tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:text-[rgba(255,255,255,0.62)] tw:min-[1023px]:[font-size:calc(1.4rem*var(--fontFit-x,1))]"
						v-bind="props.decor.bind('kso_row_label', { discount: true })"
					>
						{{ props.discountLabel }}
					</p>
					<p
						class="kso_created_accent tw:m-0 tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:text-[rgba(255,255,255,0.62)] tw:[font-family:var(--fontMain)] tw:[font-weight:normal] tw:text-[color:var(--kso-created-accent-color,#fff)]! tw:min-[1023px]:[font-size:calc(1.4rem*var(--fontFit-x,1))]"
						v-bind="props.decor.bind('kso_row_value', { discount: true })"
					>
						<template v-if="!props.surcharge">&minus;</template><slot name="discount-value"><span>{{ props.discountValue }}</span></slot>
					</p>
				</li>
				<li v-if="props.showDelivery" class="tw:flex tw:items-baseline tw:justify-between tw:gap-12" v-bind="props.decor.bind('kso_row')">
					<p
						class="tw:m-0 tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:text-[rgba(255,255,255,0.62)] tw:min-[1023px]:[font-size:calc(1.4rem*var(--fontFit-x,1))]"
						v-bind="props.decor.bind('kso_row_label')"
					>
						{{ props.deliveryLabel }}
					</p>
					<p
						class="kso_created_accent tw:m-0 tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:text-[rgba(255,255,255,0.62)] tw:[font-family:var(--fontMain)] tw:[font-weight:normal] tw:text-[color:var(--kso-created-accent-color,#fff)]! tw:min-[1023px]:[font-size:calc(1.4rem*var(--fontFit-x,1))]"
						v-bind="props.decor.bind('kso_row_value')"
					>
						<slot name="delivery-value"><span>{{ props.deliveryValue }}</span></slot>
					</p>
				</li>
				<li v-if="props.showBonuses" class="tw:flex tw:items-baseline tw:justify-between tw:gap-12" v-bind="props.decor.bind('kso_row')">
					<p
						class="tw:m-0 tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:text-[rgba(255,255,255,0.62)] tw:min-[1023px]:[font-size:calc(1.4rem*var(--fontFit-x,1))]"
						v-bind="props.decor.bind('kso_row_label')"
					>
						{{ props.bonusesLabel }}
					</p>
					<p
						class="kso_created_accent tw:m-0 tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:text-[rgba(255,255,255,0.62)] tw:[font-family:var(--fontMain)] tw:[font-weight:normal] tw:text-[color:var(--kso-created-accent-color,#fff)]! tw:min-[1023px]:[font-size:calc(1.4rem*var(--fontFit-x,1))]"
						v-bind="props.decor.bind('kso_row_value')"
					>
						{{ MINUS }}<slot name="bonuses-value"><span>{{ props.bonusesValue }}</span></slot>
					</p>
				</li>
				<li class="kso_created_total tw:flex tw:items-baseline tw:justify-between tw:gap-12" v-bind="props.decor.bind('kso_row', { total: true })">
					<p
						class="tw:m-0 tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:text-[rgba(255,255,255,0.62)] tw:[font-family:var(--fontMain)] tw:[font-weight:normal] tw:text-[length:var(--kso-created-total-size,1.15rem)]! tw:text-[color:var(--kso-created-total-color,#fff)]! tw:min-[1023px]:[font-size:calc(1.4rem*var(--fontFit-x,1))] tw:min-[1023px]:text-[length:var(--kso-created-total-size,1.7rem)]!"
						v-bind="props.decor.bind('kso_row_label', { total: true })"
					>
						{{ props.totalLabel }}
					</p>
					<p
						class="tw:m-0 tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:text-[rgba(255,255,255,0.62)] tw:[font-family:var(--fontMain)] tw:[font-weight:normal] tw:text-[length:var(--kso-created-total-size,1.15rem)]! tw:text-[color:var(--kso-created-total-color,#fff)]! tw:min-[1023px]:[font-size:calc(1.4rem*var(--fontFit-x,1))] tw:min-[1023px]:text-[length:var(--kso-created-total-size,1.7rem)]!"
						v-bind="props.decor.bind('kso_row_value', { total: true })"
					>
						<slot name="total-value"><span>{{ props.totalValue }}</span></slot>
					</p>
				</li>
				<li v-if="props.bonusesOpen" class="tw:flex tw:items-baseline tw:justify-between tw:gap-12" v-bind="props.decor.bind('kso_row', { available: true })">
					<p
						class="tw:m-0 tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:text-[rgba(255,255,255,0.62)] tw:min-[1023px]:[font-size:calc(1.4rem*var(--fontFit-x,1))]"
						v-bind="props.decor.bind('kso_row_label', { available: true })"
					>
						{{ props.availableLabel }}
					</p>
					<p
						class="kso_created_accent tw:m-0 tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:text-[rgba(255,255,255,0.62)] tw:[font-family:var(--fontMain)] tw:[font-weight:normal] tw:text-[color:var(--kso-created-accent-color,#fff)]! tw:min-[1023px]:[font-size:calc(1.4rem*var(--fontFit-x,1))]"
						v-bind="props.decor.bind('kso_row_value', { available: true })"
					>
						<slot name="available-value"><span>{{ props.availableValue }}</span></slot>
					</p>
				</li>
			</ul>

			<Transition
				name="kso_expand"
				enter-active-class="kso_expand-enter-active tw:[transition:opacity_0.3s_ease,transform_0.3s_ease]"
				leave-active-class="kso_expand-leave-active tw:absolute tw:left-24 tw:right-24 tw:pointer-events-none tw:invisible"
				enter-from-class="kso_expand-enter-from tw:opacity-0 tw:[transform:translateY(8px)]"
			>
				<slot v-if="props.bonusesOpen" name="bonuses-pad" />
			</Transition>
		</div>
	</div>
</template>
